import logger from '../log';
import {
  ObjectInterceptor,
  ObjectResponseMapper,
  ObjectRequest,
  ObjectResponse
} from './interfaces';
import composeResponseMappers from './composeResponseMappers';
import { FetchFailed } from './errors';
import InterceptorType from './InterceptorType';
import EventEmitter from '../common/EventEmitter';
import { InternalEvents, RequestIngestData } from '../events';
import { isArrayBuffer } from '../common/utils/stringify';
import isAllowedRangeRequest from './utils/isAllowedRangeRequest';
import fetchForObject from './fetchForObject';
import ContentConfig from '../config/ContentConfig';
import dispatchErrorIngest from './utils/dispatchErrorIngest';
import { SourceType } from '../webrtc/agent/interfaces';

const log = logger.getLogger(__filename);

const { REQUEST_INGEST } = InternalEvents;

export default class HttpInterceptor implements ObjectInterceptor {
  responseMapper: ObjectResponseMapper | undefined;
  readonly interceptorType = InterceptorType.HTTP;
  eventEmitter: EventEmitter;
  readonly config: ContentConfig;

  constructor(config: ContentConfig) {
    this.eventEmitter = EventEmitter.getInstance();
    this.config = config;
  }

  registerResponseMapper(mapper: ObjectResponseMapper): void {
    if (this.responseMapper) {
      this.responseMapper = composeResponseMappers(this.responseMapper, mapper);
    } else {
      this.responseMapper = mapper;
    }
  }

  async handle(request: ObjectRequest): Promise<ObjectResponse> {
    try {
      const response = await fetchForObject(request);
      const data = response.body;
      // Send the response as a message to allow for caching and transfer records
      // Need to clone array buffer for caching.
      const byteLength = isArrayBuffer(data) && data.byteLength;
      if (byteLength) {
        log.debug('sourcing', byteLength, {
          response,
          url: request.url
        });
      }
      if (isAllowedRangeRequest(this.config.app as string, request.headers)) {
        const eventData: RequestIngestData = byteLength
          ? {
              type: REQUEST_INGEST,
              data: { ...response, body: (data as ArrayBuffer).slice(0) }
            }
          : { type: REQUEST_INGEST, data: response };
        this.eventEmitter.dispatch(eventData);
      } else {
        log.info('Not ingesting range request:', request.url);
      }
      return this.responseMapper ? this.responseMapper(response) : response;
    } catch (e) {
      dispatchErrorIngest({
        type: REQUEST_INGEST,
        request,
        eventEmitter: this.eventEmitter,
        sourceType: SourceType.INGEST,
        err: e
      });
      // notify failure
      return Promise.reject(new FetchFailed(request, e as Error));
    }
  }
}
